import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { complianceAPI, authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { FileText, User, Building2, Users, Shield, Upload, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';

const COUNTRIES = [
  { code: 'US', name: 'United States' }, { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' }, { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' }, { code: 'FR', name: 'France' },
  { code: 'NO', name: 'Norway' }, { code: 'SE', name: 'Sweden' },
  { code: 'DK', name: 'Denmark' }, { code: 'FI', name: 'Finland' },
  { code: 'NL', name: 'Netherlands' }, { code: 'BE', name: 'Belgium' },
  { code: 'ES', name: 'Spain' }, { code: 'IT', name: 'Italy' },
  { code: 'CH', name: 'Switzerland' }, { code: 'AT', name: 'Austria' },
  { code: 'IE', name: 'Ireland' }, { code: 'PT', name: 'Portugal' },
  { code: 'PL', name: 'Poland' }, { code: 'IN', name: 'India' },
  { code: 'CN', name: 'China' }, { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' }, { code: 'SG', name: 'Singapore' },
  { code: 'AE', name: 'United Arab Emirates' }, { code: 'SA', name: 'Saudi Arabia' },
  { code: 'NG', name: 'Nigeria' }, { code: 'KE', name: 'Kenya' },
  { code: 'ZA', name: 'South Africa' }, { code: 'EG', name: 'Egypt' },
  { code: 'BR', name: 'Brazil' }, { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' }, { code: 'CL', name: 'Chile' },
];

const DOCUMENT_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'national_id', label: 'National ID Card' },
  { value: 'drivers_license', label: 'Driver\'s License' },
  { value: 'proof_of_address', label: 'Proof of Address' },
  { value: 'registration_extract', label: 'Company Registration Extract' },
  { value: 'corporate_structure', label: 'Corporate Structure Chart' },
  { value: 'financial_statement', label: 'Financial Statement' },
  { value: 'ownership_proof', label: 'Proof of Ownership' },
];

export default function KYCApplication() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [kycApplication, setKycApplication] = useState(null);
  const [activeTab, setActiveTab] = useState('personal');
  
  // Form states
  const [customerType, setCustomerType] = useState('');
  const [individualData, setIndividualData] = useState({
    first_name: '', last_name: '', date_of_birth: '', nationality: '',
    country_of_residence: '', phone: '', email: '', occupation: '',
    employer: '', source_of_funds: '', expected_monthly_volume: '',
    expected_monthly_transactions: '', purpose_of_account: '',
    is_pep: false, pep_details: '',
    address: { street: '', city: '', state: '', postal_code: '', country: '' }
  });
  const [corporateData, setCorporateData] = useState({
    legal_name: '', trading_name: '', registration_number: '', legal_form: '',
    date_of_incorporation: '', country_of_incorporation: '', industry_sector: '',
    business_description: '', website: '', phone: '', email: '',
    expected_monthly_volume: '', expected_monthly_transactions: '',
    main_countries_of_operation: [], products_services: '',
    source_of_funds: '', purpose_of_account: '',
    registered_address: { street: '', city: '', state: '', postal_code: '', country: '' },
    business_address: { street: '', city: '', state: '', postal_code: '', country: '' }
  });
  const [ubos, setUbos] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [pepDeclarations, setPepDeclarations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const userRes = await authAPI.getMe();
      setCurrentUser(userRes.data);
      
      // Try to get existing KYC application
      const kycRes = await complianceAPI.getUserKYC(userRes.data.id);
      if (kycRes.data) {
        setKycApplication(kycRes.data);
        setCustomerType(kycRes.data.customer_type);
        if (kycRes.data.individual_data) {
          setIndividualData(kycRes.data.individual_data);
        }
        if (kycRes.data.corporate_data) {
          setCorporateData(kycRes.data.corporate_data);
        }
        setUbos(kycRes.data.ubos || []);
        setDirectors(kycRes.data.directors || []);
        setPepDeclarations(kycRes.data.pep_declarations || []);
        setDocuments(kycRes.data.documents || []);
      }
    } catch (error) {
      // No existing application, that's okay
    } finally {
      setLoading(false);
    }
  };

  const startApplication = async (type) => {
    setSaving(true);
    try {
      const res = await complianceAPI.startKYC(type);
      setCustomerType(type);
      setKycApplication({ id: res.data.kyc_id, status: 'draft' });
      toast.success('KYC application started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start application');
    } finally {
      setSaving(false);
    }
  };

  const saveIndividualData = async () => {
    if (!kycApplication?.id) return;
    setSaving(true);
    try {
      await complianceAPI.updateIndividual(kycApplication.id, {
        ...individualData,
        expected_monthly_volume: parseFloat(individualData.expected_monthly_volume) || 0,
        expected_monthly_transactions: parseInt(individualData.expected_monthly_transactions) || 0,
      });
      toast.success('Personal information saved');
      setActiveTab('documents');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveCorporateData = async () => {
    if (!kycApplication?.id) return;
    setSaving(true);
    try {
      await complianceAPI.updateCorporate(kycApplication.id, {
        ...corporateData,
        expected_monthly_volume: parseFloat(corporateData.expected_monthly_volume) || 0,
        expected_monthly_transactions: parseInt(corporateData.expected_monthly_transactions) || 0,
      });
      toast.success('Company information saved');
      setActiveTab('ubo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addUBO = async (uboData) => {
    if (!kycApplication?.id) return;
    setSaving(true);
    try {
      const res = await complianceAPI.addUBO(kycApplication.id, uboData);
      setUbos([...ubos, { ...uboData, id: res.data.ubo_id }]);
      toast.success('UBO added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add UBO');
    } finally {
      setSaving(false);
    }
  };

  const addDirector = async (directorData) => {
    if (!kycApplication?.id) return;
    setSaving(true);
    try {
      const res = await complianceAPI.addDirector(kycApplication.id, directorData);
      setDirectors([...directors, { ...directorData, id: res.data.director_id }]);
      toast.success('Director added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add director');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file || !kycApplication?.id) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    
    try {
      const res = await complianceAPI.uploadDocument(kycApplication.id, formData);
      setDocuments([...documents, {
        id: res.data.document_id,
        document_type: docType,
        file_name: file.name,
        verified: false
      }]);
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const submitApplication = async () => {
    if (!kycApplication?.id) return;
    setSaving(true);
    try {
      const res = await complianceAPI.submitKYC(kycApplication.id);
      setKycApplication({ ...kycApplication, status: res.data.status });
      toast.success(`Application submitted! Risk level: ${res.data.risk_level}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { variant: 'secondary', icon: FileText, label: 'Draft' },
      submitted: { variant: 'default', icon: Clock, label: 'Submitted' },
      under_review: { variant: 'default', icon: Clock, label: 'Under Review' },
      pending_documents: { variant: 'secondary', icon: AlertTriangle, label: 'Pending Documents' },
      pending_approval: { variant: 'default', icon: Clock, label: 'Pending Approval' },
      approved: { variant: 'default', icon: CheckCircle2, label: 'Approved' },
      rejected: { variant: 'destructive', icon: AlertTriangle, label: 'Rejected' },
      blocked: { variant: 'destructive', icon: AlertTriangle, label: 'Blocked' },
    };
    const config = statusConfig[status] || { variant: 'outline', icon: FileText, label: status };
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  // Application is already submitted
  if (kycApplication && kycApplication.status !== 'draft') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Card className="bg-white border">
            <CardHeader className="text-center">
              <CardTitle className="text-gray-900">KYC Application Status</CardTitle>
              <CardDescription>Your application is being processed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-center">
                {getStatusBadge(kycApplication.status)}
              </div>
              
              {kycApplication.risk_assessment && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-gray-900 font-medium mb-2">Risk Assessment</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-gray-600">Risk Level:</div>
                    <div>
                      <Badge variant={
                        kycApplication.risk_assessment.overall_risk === 'low' ? 'default' :
                        kycApplication.risk_assessment.overall_risk === 'medium' ? 'secondary' : 'destructive'
                      }>
                        {kycApplication.risk_assessment.overall_risk?.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="text-gray-600">Risk Score:</div>
                    <div className="text-gray-900">{kycApplication.risk_assessment.risk_score}/100</div>
                  </div>
                </div>
              )}

              {kycApplication.status === 'pending_documents' && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm">
                    Additional documents are required. Please upload the requested documents.
                  </p>
                </div>
              )}

              {kycApplication.status === 'rejected' && kycApplication.rejection_reason && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">
                    <strong>Rejection Reason:</strong> {kycApplication.rejection_reason}
                  </p>
                </div>
              )}

              <div className="text-center text-gray-600 text-sm">
                <p>Submitted: {new Date(kycApplication.submitted_at).toLocaleDateString()}</p>
                {kycApplication.next_review_date && (
                  <p>Next Review: {new Date(kycApplication.next_review_date).toLocaleDateString()}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // No application started yet - show type selection
  if (!customerType) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">KYC Application</h1>
            <p className="text-gray-600">Complete your Know Your Customer verification to start using EzyTransfer</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white border hover:border-emerald-500/50 transition-colors cursor-pointer"
                  onClick={() => startApplication('individual')} data-testid="individual-kyc-btn">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-4">
                  <User className="h-8 w-8 text-emerald-500" />
                </div>
                <CardTitle className="text-gray-900">Individual Account</CardTitle>
                <CardDescription>For personal use and individual transfers</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>Personal identification</li>
                  <li>Proof of address</li>
                  <li>Source of funds declaration</li>
                  <li>PEP declaration</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border hover:border-emerald-500/50 transition-colors cursor-pointer"
                  onClick={() => startApplication('corporate')} data-testid="corporate-kyc-btn">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4">
                  <Building2 className="h-8 w-8 text-blue-500" />
                </div>
                <CardTitle className="text-gray-900">Corporate Account</CardTitle>
                <CardDescription>For businesses and organizations</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>Company registration documents</li>
                  <li>UBO declaration (10% threshold)</li>
                  <li>Directors and authorized signatories</li>
                  <li>Financial statements</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Application form
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {customerType === 'individual' ? 'Individual' : 'Corporate'} KYC Application
            </h1>
            <p className="text-gray-600">Complete all required sections</p>
          </div>
          {getStatusBadge(kycApplication?.status || 'draft')}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 bg-gray-50">
            <TabsTrigger value="personal" className="data-[state=active]:bg-emerald-600">
              {customerType === 'individual' ? 'Personal Info' : 'Company Info'}
            </TabsTrigger>
            {customerType === 'corporate' && (
              <TabsTrigger value="ubo" className="data-[state=active]:bg-emerald-600">UBO & Directors</TabsTrigger>
            )}
            <TabsTrigger value="documents" className="data-[state=active]:bg-emerald-600">Documents</TabsTrigger>
            <TabsTrigger value="review" className="data-[state=active]:bg-emerald-600">Review & Submit</TabsTrigger>
          </TabsList>

          {/* Individual Personal Info */}
          {customerType === 'individual' && (
            <TabsContent value="personal">
              <Card className="bg-white border">
                <CardHeader>
                  <CardTitle className="text-gray-900">Personal Information</CardTitle>
                  <CardDescription>Your personal details for verification</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">First Name *</Label>
                      <Input
                        value={individualData.first_name}
                        onChange={(e) => setIndividualData({ ...individualData, first_name: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                        data-testid="first-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Last Name *</Label>
                      <Input
                        value={individualData.last_name}
                        onChange={(e) => setIndividualData({ ...individualData, last_name: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                        data-testid="last-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Date of Birth *</Label>
                      <Input
                        type="date"
                        value={individualData.date_of_birth}
                        onChange={(e) => setIndividualData({ ...individualData, date_of_birth: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                        data-testid="dob-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Nationality *</Label>
                      <Select
                        value={individualData.nationality}
                        onValueChange={(v) => setIndividualData({ ...individualData, nationality: v })}
                      >
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Country of Residence *</Label>
                      <Select
                        value={individualData.country_of_residence}
                        onValueChange={(v) => setIndividualData({ ...individualData, country_of_residence: v })}
                      >
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Phone *</Label>
                      <Input
                        value={individualData.phone}
                        onChange={(e) => setIndividualData({ ...individualData, phone: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                        placeholder="+1234567890"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Email *</Label>
                      <Input
                        type="email"
                        value={individualData.email}
                        onChange={(e) => setIndividualData({ ...individualData, email: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Occupation *</Label>
                      <Input
                        value={individualData.occupation}
                        onChange={(e) => setIndividualData({ ...individualData, occupation: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Employer</Label>
                      <Input
                        value={individualData.employer}
                        onChange={(e) => setIndividualData({ ...individualData, employer: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-gray-900 font-medium mb-4">Address</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-gray-700">Street Address *</Label>
                        <Input
                          value={individualData.address.street}
                          onChange={(e) => setIndividualData({
                            ...individualData,
                            address: { ...individualData.address, street: e.target.value }
                          })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">City *</Label>
                        <Input
                          value={individualData.address.city}
                          onChange={(e) => setIndividualData({
                            ...individualData,
                            address: { ...individualData.address, city: e.target.value }
                          })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">State/Province</Label>
                        <Input
                          value={individualData.address.state}
                          onChange={(e) => setIndividualData({
                            ...individualData,
                            address: { ...individualData.address, state: e.target.value }
                          })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Postal Code *</Label>
                        <Input
                          value={individualData.address.postal_code}
                          onChange={(e) => setIndividualData({
                            ...individualData,
                            address: { ...individualData.address, postal_code: e.target.value }
                          })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Country *</Label>
                        <Select
                          value={individualData.address.country}
                          onValueChange={(v) => setIndividualData({
                            ...individualData,
                            address: { ...individualData.address, country: v }
                          })}
                        >
                          <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-gray-900 font-medium mb-4">Financial Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Source of Funds *</Label>
                        <Select
                          value={individualData.source_of_funds}
                          onValueChange={(v) => setIndividualData({ ...individualData, source_of_funds: v })}
                        >
                          <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                            <SelectValue placeholder="Select source" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employment">Employment Income</SelectItem>
                            <SelectItem value="business">Business Income</SelectItem>
                            <SelectItem value="investments">Investments</SelectItem>
                            <SelectItem value="inheritance">Inheritance</SelectItem>
                            <SelectItem value="savings">Personal Savings</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Purpose of Account *</Label>
                        <Select
                          value={individualData.purpose_of_account}
                          onValueChange={(v) => setIndividualData({ ...individualData, purpose_of_account: v })}
                        >
                          <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                            <SelectValue placeholder="Select purpose" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="personal_remittance">Personal Remittance</SelectItem>
                            <SelectItem value="family_support">Family Support</SelectItem>
                            <SelectItem value="bill_payments">Bill Payments</SelectItem>
                            <SelectItem value="business_payments">Business Payments</SelectItem>
                            <SelectItem value="investments">Investments</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Expected Monthly Volume (USD) *</Label>
                        <Input
                          type="number"
                          value={individualData.expected_monthly_volume}
                          onChange={(e) => setIndividualData({ ...individualData, expected_monthly_volume: e.target.value })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                          placeholder="e.g., 5000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Expected Monthly Transactions *</Label>
                        <Input
                          type="number"
                          value={individualData.expected_monthly_transactions}
                          onChange={(e) => setIndividualData({ ...individualData, expected_monthly_transactions: e.target.value })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                          placeholder="e.g., 5"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-gray-900 font-medium mb-4">PEP Declaration</h3>
                    <div className="flex items-center space-x-2 mb-4">
                      <Checkbox
                        id="is-pep"
                        checked={individualData.is_pep}
                        onCheckedChange={(checked) => setIndividualData({ ...individualData, is_pep: checked })}
                      />
                      <Label htmlFor="is-pep" className="text-gray-700">
                        I am a Politically Exposed Person (PEP) or related to a PEP
                      </Label>
                    </div>
                    {individualData.is_pep && (
                      <div className="space-y-2">
                        <Label className="text-gray-700">Please provide details</Label>
                        <Textarea
                          value={individualData.pep_details}
                          onChange={(e) => setIndividualData({ ...individualData, pep_details: e.target.value })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                          placeholder="Describe your position or relationship to a PEP..."
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={saveIndividualData} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save & Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Corporate Company Info */}
          {customerType === 'corporate' && (
            <TabsContent value="personal">
              <Card className="bg-white border">
                <CardHeader>
                  <CardTitle className="text-gray-900">Company Information</CardTitle>
                  <CardDescription>Business details for verification</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Legal Name *</Label>
                      <Input
                        value={corporateData.legal_name}
                        onChange={(e) => setCorporateData({ ...corporateData, legal_name: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Trading Name</Label>
                      <Input
                        value={corporateData.trading_name}
                        onChange={(e) => setCorporateData({ ...corporateData, trading_name: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Registration Number *</Label>
                      <Input
                        value={corporateData.registration_number}
                        onChange={(e) => setCorporateData({ ...corporateData, registration_number: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Legal Form *</Label>
                      <Select
                        value={corporateData.legal_form}
                        onValueChange={(v) => setCorporateData({ ...corporateData, legal_form: v })}
                      >
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LLC">Limited Liability Company (LLC)</SelectItem>
                          <SelectItem value="Corporation">Corporation</SelectItem>
                          <SelectItem value="Partnership">Partnership</SelectItem>
                          <SelectItem value="Sole Proprietorship">Sole Proprietorship</SelectItem>
                          <SelectItem value="Non-Profit">Non-Profit Organization</SelectItem>
                          <SelectItem value="Trust">Trust</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Date of Incorporation *</Label>
                      <Input
                        type="date"
                        value={corporateData.date_of_incorporation}
                        onChange={(e) => setCorporateData({ ...corporateData, date_of_incorporation: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Country of Incorporation *</Label>
                      <Select
                        value={corporateData.country_of_incorporation}
                        onValueChange={(v) => setCorporateData({ ...corporateData, country_of_incorporation: v })}
                      >
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Industry Sector *</Label>
                      <Input
                        value={corporateData.industry_sector}
                        onChange={(e) => setCorporateData({ ...corporateData, industry_sector: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                        placeholder="e.g., Financial Services"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Website</Label>
                      <Input
                        value={corporateData.website}
                        onChange={(e) => setCorporateData({ ...corporateData, website: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                        placeholder="https://"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Phone *</Label>
                      <Input
                        value={corporateData.phone}
                        onChange={(e) => setCorporateData({ ...corporateData, phone: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Email *</Label>
                      <Input
                        type="email"
                        value={corporateData.email}
                        onChange={(e) => setCorporateData({ ...corporateData, email: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-gray-900"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700">Business Description *</Label>
                    <Textarea
                      value={corporateData.business_description}
                      onChange={(e) => setCorporateData({ ...corporateData, business_description: e.target.value })}
                      className="bg-gray-50 border-gray-200 text-gray-900"
                      placeholder="Describe your business activities..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700">Products/Services *</Label>
                    <Textarea
                      value={corporateData.products_services}
                      onChange={(e) => setCorporateData({ ...corporateData, products_services: e.target.value })}
                      className="bg-gray-50 border-gray-200 text-gray-900"
                      placeholder="Describe your products and services..."
                    />
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-gray-900 font-medium mb-4">Registered Address</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-gray-700">Street Address *</Label>
                        <Input
                          value={corporateData.registered_address.street}
                          onChange={(e) => setCorporateData({
                            ...corporateData,
                            registered_address: { ...corporateData.registered_address, street: e.target.value }
                          })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">City *</Label>
                        <Input
                          value={corporateData.registered_address.city}
                          onChange={(e) => setCorporateData({
                            ...corporateData,
                            registered_address: { ...corporateData.registered_address, city: e.target.value }
                          })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Postal Code *</Label>
                        <Input
                          value={corporateData.registered_address.postal_code}
                          onChange={(e) => setCorporateData({
                            ...corporateData,
                            registered_address: { ...corporateData.registered_address, postal_code: e.target.value }
                          })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Country *</Label>
                        <Select
                          value={corporateData.registered_address.country}
                          onValueChange={(v) => setCorporateData({
                            ...corporateData,
                            registered_address: { ...corporateData.registered_address, country: v }
                          })}
                        >
                          <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-gray-900 font-medium mb-4">Financial Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Source of Funds *</Label>
                        <Select
                          value={corporateData.source_of_funds}
                          onValueChange={(v) => setCorporateData({ ...corporateData, source_of_funds: v })}
                        >
                          <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                            <SelectValue placeholder="Select source" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="business_revenue">Business Revenue</SelectItem>
                            <SelectItem value="investment">Investment Proceeds</SelectItem>
                            <SelectItem value="loans">Loans/Credit</SelectItem>
                            <SelectItem value="capital">Shareholder Capital</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Purpose of Account *</Label>
                        <Select
                          value={corporateData.purpose_of_account}
                          onValueChange={(v) => setCorporateData({ ...corporateData, purpose_of_account: v })}
                        >
                          <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                            <SelectValue placeholder="Select purpose" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="supplier_payments">Supplier Payments</SelectItem>
                            <SelectItem value="payroll">Payroll</SelectItem>
                            <SelectItem value="international_trade">International Trade</SelectItem>
                            <SelectItem value="treasury">Treasury Management</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Expected Monthly Volume (USD) *</Label>
                        <Input
                          type="number"
                          value={corporateData.expected_monthly_volume}
                          onChange={(e) => setCorporateData({ ...corporateData, expected_monthly_volume: e.target.value })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Expected Monthly Transactions *</Label>
                        <Input
                          type="number"
                          value={corporateData.expected_monthly_transactions}
                          onChange={(e) => setCorporateData({ ...corporateData, expected_monthly_transactions: e.target.value })}
                          className="bg-gray-50 border-gray-200 text-gray-900"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={saveCorporateData} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save & Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* UBO & Directors (Corporate only) */}
          {customerType === 'corporate' && (
            <TabsContent value="ubo">
              <UBODirectorsSection
                ubos={ubos}
                directors={directors}
                onAddUBO={addUBO}
                onAddDirector={addDirector}
                saving={saving}
              />
            </TabsContent>
          )}

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="bg-white border">
              <CardHeader>
                <CardTitle className="text-gray-900">Document Upload</CardTitle>
                <CardDescription>Upload required verification documents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  {DOCUMENT_TYPES.filter(dt => 
                    customerType === 'individual' 
                      ? ['passport', 'national_id', 'drivers_license', 'proof_of_address'].includes(dt.value)
                      : true
                  ).map((docType) => (
                    <div key={docType.value} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-900 font-medium">{docType.label}</span>
                        {documents.find(d => d.document_type === docType.value) ? (
                          <Badge variant="default">Uploaded</Badge>
                        ) : (
                          <Badge variant="secondary">Required</Badge>
                        )}
                      </div>
                      {documents.find(d => d.document_type === docType.value) ? (
                        <p className="text-sm text-gray-600">
                          {documents.find(d => d.document_type === docType.value)?.file_name}
                        </p>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleDocumentUpload(e, docType.value)}
                            disabled={uploading}
                          />
                          <div className="flex items-center gap-2 text-emerald-500 hover:text-emerald-400">
                            <Upload className="h-4 w-4" />
                            <span className="text-sm">Upload file</span>
                          </div>
                        </label>
                      )}
                    </div>
                  ))}
                </div>

                {uploading && (
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Uploading...</span>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={() => setActiveTab('review')} className="bg-emerald-600 hover:bg-emerald-700">
                    Continue to Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Review & Submit */}
          <TabsContent value="review">
            <Card className="bg-white border">
              <CardHeader>
                <CardTitle className="text-gray-900">Review & Submit</CardTitle>
                <CardDescription>Review your application before submitting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-gray-900 font-medium mb-2">Application Summary</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-gray-600">Account Type:</div>
                    <div className="text-gray-900 capitalize">{customerType}</div>
                    {customerType === 'individual' ? (
                      <>
                        <div className="text-gray-600">Name:</div>
                        <div className="text-gray-900">{individualData.first_name} {individualData.last_name}</div>
                        <div className="text-gray-600">Country:</div>
                        <div className="text-gray-900">{individualData.country_of_residence}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-gray-600">Company:</div>
                        <div className="text-gray-900">{corporateData.legal_name}</div>
                        <div className="text-gray-600">Country:</div>
                        <div className="text-gray-900">{corporateData.country_of_incorporation}</div>
                        <div className="text-gray-600">UBOs:</div>
                        <div className="text-gray-900">{ubos.length}</div>
                        <div className="text-gray-600">Directors:</div>
                        <div className="text-gray-900">{directors.length}</div>
                      </>
                    )}
                    <div className="text-gray-600">Documents:</div>
                    <div className="text-gray-900">{documents.length} uploaded</div>
                  </div>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">Before you submit:</p>
                      <ul className="list-disc list-inside space-y-1 text-amber-300">
                        <li>Ensure all information is accurate and complete</li>
                        <li>Upload all required documents</li>
                        <li>Review PEP declarations carefully</li>
                        <li>Application cannot be edited after submission</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="confirm" />
                  <Label htmlFor="confirm" className="text-gray-700 text-sm">
                    I confirm that all information provided is true and accurate
                  </Label>
                </div>

                <div className="flex justify-end">
                  <Button 
                    onClick={submitApplication} 
                    disabled={saving} 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="submit-kyc-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Submit Application
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// UBO & Directors Section Component
function UBODirectorsSection({ ubos, directors, onAddUBO, onAddDirector, saving }) {
  const [showUBOForm, setShowUBOForm] = useState(false);
  const [showDirectorForm, setShowDirectorForm] = useState(false);
  const [uboForm, setUboForm] = useState({
    full_name: '', date_of_birth: '', nationality: '', country_of_residence: '',
    ownership_percentage: '', ownership_type: 'direct', is_control_based: false,
    control_description: '', is_pep: false, pep_details: '',
    address: { street: '', city: '', state: '', postal_code: '', country: '' }
  });
  const [directorForm, setDirectorForm] = useState({
    full_name: '', position: '', date_of_birth: '', nationality: '',
    is_authorized_signatory: false, is_pep: false, pep_details: ''
  });

  const handleAddUBO = async () => {
    await onAddUBO({
      ...uboForm,
      ownership_percentage: parseFloat(uboForm.ownership_percentage) || 0
    });
    setShowUBOForm(false);
    setUboForm({
      full_name: '', date_of_birth: '', nationality: '', country_of_residence: '',
      ownership_percentage: '', ownership_type: 'direct', is_control_based: false,
      control_description: '', is_pep: false, pep_details: '',
      address: { street: '', city: '', state: '', postal_code: '', country: '' }
    });
  };

  const handleAddDirector = async () => {
    await onAddDirector(directorForm);
    setShowDirectorForm(false);
    setDirectorForm({
      full_name: '', position: '', date_of_birth: '', nationality: '',
      is_authorized_signatory: false, is_pep: false, pep_details: ''
    });
  };

  return (
    <div className="space-y-6">
      {/* UBOs Section */}
      <Card className="bg-white border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-gray-900">Ultimate Beneficial Owners (UBOs)</CardTitle>
              <CardDescription>Individuals who own 10% or more of the company</CardDescription>
            </div>
            <Button onClick={() => setShowUBOForm(true)} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-1" /> Add UBO
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {ubos.length === 0 ? (
            <p className="text-gray-600 text-center py-4">No UBOs added yet. At least one UBO is required.</p>
          ) : (
            <div className="space-y-3">
              {ubos.map((ubo, idx) => (
                <div key={ubo.id || idx} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-900 font-medium">{ubo.full_name}</p>
                      <p className="text-sm text-gray-600">
                        {ubo.ownership_percentage}% ownership ({ubo.ownership_type})
                        {ubo.is_pep && <Badge variant="secondary" className="ml-2">PEP</Badge>}
                      </p>
                    </div>
                    <Badge variant="outline">{ubo.nationality}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showUBOForm && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
              <h4 className="text-gray-900 font-medium">Add New UBO</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Full Name *</Label>
                  <Input
                    value={uboForm.full_name}
                    onChange={(e) => setUboForm({ ...uboForm, full_name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-gray-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Date of Birth *</Label>
                  <Input
                    type="date"
                    value={uboForm.date_of_birth}
                    onChange={(e) => setUboForm({ ...uboForm, date_of_birth: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-gray-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Nationality *</Label>
                  <Select
                    value={uboForm.nationality}
                    onValueChange={(v) => setUboForm({ ...uboForm, nationality: v })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-gray-900">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Ownership % *</Label>
                  <Input
                    type="number"
                    min="10"
                    max="100"
                    value={uboForm.ownership_percentage}
                    onChange={(e) => setUboForm({ ...uboForm, ownership_percentage: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-gray-900"
                    placeholder="Min 10%"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Ownership Type *</Label>
                  <Select
                    value={uboForm.ownership_type}
                    onValueChange={(v) => setUboForm({ ...uboForm, ownership_type: v })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Direct</SelectItem>
                      <SelectItem value="indirect">Indirect</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Country of Residence *</Label>
                  <Select
                    value={uboForm.country_of_residence}
                    onValueChange={(v) => setUboForm({ ...uboForm, country_of_residence: v })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-gray-900">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={uboForm.is_pep}
                  onCheckedChange={(checked) => setUboForm({ ...uboForm, is_pep: checked })}
                />
                <Label className="text-gray-700">This UBO is a PEP</Label>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowUBOForm(false)}>Cancel</Button>
                <Button onClick={handleAddUBO} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Add UBO
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Directors Section */}
      <Card className="bg-white border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-gray-900">Directors & Authorized Signatories</CardTitle>
              <CardDescription>Company directors and people authorized to act on behalf of the company</CardDescription>
            </div>
            <Button onClick={() => setShowDirectorForm(true)} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-1" /> Add Director
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {directors.length === 0 ? (
            <p className="text-gray-600 text-center py-4">No directors added yet.</p>
          ) : (
            <div className="space-y-3">
              {directors.map((dir, idx) => (
                <div key={dir.id || idx} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-900 font-medium">{dir.full_name}</p>
                      <p className="text-sm text-gray-600">
                        {dir.position}
                        {dir.is_authorized_signatory && <Badge variant="default" className="ml-2">Signatory</Badge>}
                        {dir.is_pep && <Badge variant="secondary" className="ml-2">PEP</Badge>}
                      </p>
                    </div>
                    <Badge variant="outline">{dir.nationality}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showDirectorForm && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
              <h4 className="text-gray-900 font-medium">Add New Director</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Full Name *</Label>
                  <Input
                    value={directorForm.full_name}
                    onChange={(e) => setDirectorForm({ ...directorForm, full_name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-gray-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Position *</Label>
                  <Select
                    value={directorForm.position}
                    onValueChange={(v) => setDirectorForm({ ...directorForm, position: v })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-gray-900">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Director">Director</SelectItem>
                      <SelectItem value="CEO">CEO</SelectItem>
                      <SelectItem value="CFO">CFO</SelectItem>
                      <SelectItem value="COO">COO</SelectItem>
                      <SelectItem value="Secretary">Secretary</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Date of Birth *</Label>
                  <Input
                    type="date"
                    value={directorForm.date_of_birth}
                    onChange={(e) => setDirectorForm({ ...directorForm, date_of_birth: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-gray-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Nationality *</Label>
                  <Select
                    value={directorForm.nationality}
                    onValueChange={(v) => setDirectorForm({ ...directorForm, nationality: v })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-gray-900">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={directorForm.is_authorized_signatory}
                    onCheckedChange={(checked) => setDirectorForm({ ...directorForm, is_authorized_signatory: checked })}
                  />
                  <Label className="text-gray-700">Authorized Signatory</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={directorForm.is_pep}
                    onCheckedChange={(checked) => setDirectorForm({ ...directorForm, is_pep: checked })}
                  />
                  <Label className="text-gray-700">PEP</Label>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowDirectorForm(false)}>Cancel</Button>
                <Button onClick={handleAddDirector} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Add Director
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
